import {useState} from "react";
import {useHookClass} from "@dvrt/graphql-widgets";
import {FormattedPageTitle} from "../../pages";
import {DataSource} from "./dataSource";
import {TimeRange, QueueCall} from "./types";
import Loading from "@/components/breadcrumbs/Loading";
import {useIntl, FormattedMessage, defineMessage} from "react-intl";
import {averageCallSetupTime, graph_data_download_in_excel} from "./tools";
import SelectTimeRange from "./SelectTimeRange";
import SelectAgent from "./SelectAgent";
import CallChart, {callCountGraph, callDurationGraph, GraphMappings} from "./CallChart";
import {Member} from "../members/types";
import {AgentStateSummary} from "./AgentStateSummary";
import FormattedButton from "@/components/FormattedButton";
import {Divider} from "@mui/material";

type Props = {
    groupId: number;
};

type SelectedMember = Member | null;

export default (props: Props) => {
    const dataSource = useHookClass(DataSource, props.groupId, true);
    const intl = useIntl();
    const [timeRange, setTimeRange] = useState<TimeRange>(TimeRange.Today);
    const [selectedMember, setSelectedMember] = useState<SelectedMember>(null);

    if (!dataSource.isInitialized) return <Loading />;

    const memberMapping: GraphMappings = {};
    const memberDurationMapping: GraphMappings = {};
    const addMappings = (member: Member) => {
        memberMapping[`${member.number.extension.displayName} (${member.number.shortNumber})`] = (calls: QueueCall[]) =>
            calls.filter(
                (c) => c.received.connected && c.agent.some((a) => a.remoteNumberId === member.numberId && a.connected),
            ).length;
        memberMapping[
            intl.formatMessage(
                {
                    id: "call_queue.statistics.graph.agent.not-answered",
                    defaultMessage: "not answered - {agentName} ({agentNumber})",
                },
                {agentName: member.number.extension.displayName, agentNumber: member.number.shortNumber},
            )
        ] = (calls: QueueCall[]) =>
            calls.filter(
                (c) =>
                    c.received.connected && c.agent.some((a) => a.remoteNumberId === member.numberId && !a.connected),
            ).length;
        memberDurationMapping[
            intl.formatMessage(
                {
                    id: "call_queue.statistics.graph.agent.talk-time",
                    defaultMessage: "talk time - {agentName} ({agentNumber})",
                },
                {agentName: member.number.extension.displayName, agentNumber: member.number.shortNumber},
            )
        ] = (calls: QueueCall[]) =>
            calls
                .map((c) =>
                    c.received.connected
                        ? c.agent.filter((a) => a.remoteNumberId === member.numberId && a.connected)
                        : [],
                )
                .flat()
                .reduce((s, c) => s + c.duration, 0);
        memberDurationMapping[
            intl.formatMessage(
                {
                    id: "call_queue.statistics.graph.agent.speed-of-answer",
                    defaultMessage: "speed of answer - {agentName} ({agentNumber})",
                },
                {agentName: member.number.extension.displayName, agentNumber: member.number.shortNumber},
            )
        ] = (calls: QueueCall[]) =>
            averageCallSetupTime(
                calls
                    .map((c) =>
                        c.received.connected
                            ? c.agent.filter((a) => a.remoteNumberId === member.numberId && a.connected)
                            : [],
                    )
                    .flat(),
            );
    };
    if (selectedMember === null) {
        for (const member of dataSource.members) {
            addMappings(member);
        }
    } else {
        addMappings(selectedMember);
    }

    const binnedCalls = dataSource.binnedCalls(timeRange);
    const binnedCallsForSummary = dataSource.binnedCalls(timeRange, true);

    return (
        <div style={{marginBottom: 20}}>
            <FormattedPageTitle
                desc={defineMessage({
                    id: "call_queue.statistics.page-title",
                    defaultMessage: "Queue Statistics for { name }",
                })}
                values={{name: dataSource.groupInfo.name}}
            />

            <SelectTimeRange timeRange={timeRange} setTimeRange={setTimeRange} />
            <SelectAgent member={selectedMember} members={dataSource.members} setSelectedMember={setSelectedMember} />
            <h3>
                <FormattedMessage id="call_queue.statistics.graph-title.agent-calls" defaultMessage="Number of Calls" />
            </h3>
            <CallChart graphType={callCountGraph} data={binnedCalls} mapping={memberMapping} timeRange={timeRange} />
            <FormattedButton
                message={defineMessage({id: "download_data", defaultMessage: "Download data"})}
                onClick={() =>
                    graph_data_download_in_excel({
                        mappings: memberMapping,
                        binnedCalls,
                        intl,
                        sheetName: defineMessage({
                            id: "call_queue.statistics.graph-title.agent-calls",
                            defaultMessage: "Number of Calls",
                        }),
                        fileName: "agent-call-counts",
                    })
                }
            />
            <Divider style={{marginTop: "2rem", marginBottom: "2rem"}} />
            <h3>
                <FormattedMessage
                    id="call_queue.statistics.graph-title.agent-call-durations"
                    defaultMessage="Duration of Calls"
                />
            </h3>
            <CallChart
                graphType={callDurationGraph}
                data={binnedCalls}
                mapping={memberDurationMapping}
                timeRange={timeRange}
            />
            <FormattedButton
                message={defineMessage({id: "download_data", defaultMessage: "Download data"})}
                onClick={() =>
                    graph_data_download_in_excel({
                        mappings: memberDurationMapping,
                        binnedCalls,
                        intl,
                        sheetName: defineMessage({
                            id: "call_queue.statistics.graph-title.agent-call-durations",
                            defaultMessage: "Duration of Calls",
                        }),
                        fileName: "agent-call-durations",
                        decimalPlaces: 2,
                    })
                }
            />
            <Divider style={{marginTop: "2rem", marginBottom: "2rem"}} />
            <h3>
                <FormattedMessage id="call_queue.statistics.agent-report.title" defaultMessage="Agent state report" />
            </h3>
            <AgentStateSummary
                states={dataSource.binStateChanges(timeRange)}
                calls={binnedCallsForSummary}
                members={selectedMember ? [selectedMember] : dataSource.members}
                loading={false}
                style={{marginTop: 10}}
                initialOrder={["~day"]}
            />
            {/* this is only used for debugging
            <h3><FormattedMessage id="call_queue.statistics.agent-states.title"/></h3>
            <AgentStates
                states={selectedMember
                    ? dataSource.agentStates.current.filter(ch => ch.agentNumberId == selectedMember.numberId)
                        : dataSource.agentStates.current}
                loading={loading}
                style={{marginTop: 10}}
                initialOrder={['~timeOfChange']}
            />
*/}
        </div>
    );
};
